import React from 'react'
import PropTypes from 'prop-types'
import { Field, FieldArray, formValueSelector } from 'redux-form'
import {
  contains,
  gt,
  __,
  any,
  ifElse,
  propSatisfies,
  F,
  T,
  propEq,
  both,
  useWith,
  none,
  and,
  cond,
  always,
  equals
} from 'ramda'
import styled from 'styled-components'
import {
  TwitterTriggers,
  InstagramTriggers,
  FacebookTriggers
} from './triggers'
import { TwitterActions, InstagramActions, FacebookActions } from './actions'
import { PanelGroup, Panel } from 'react-bootstrap'
import { RadioGroup } from 'react-icheck'
import withFieldError from 'components/form/with-error'
import { connect } from 'react-redux'

export const RadioGroupField = ({
  input: { value, onChange, name },
  meta,
  children,
  ...props
}) => (
  <RadioGroup name={name} value={value} onChange={onChange} {...props}>
    {children}
  </RadioGroup>
)

RadioGroupField.propTypes = {
  input: PropTypes.any,
  meta: PropTypes.any,
  children: PropTypes.any
}

export const SetName = ({ input, meta: { error, touched }, onClick }) => (
  <input
    placeholder='Trigger and action set name'
    className={
      error && touched ? 'has-error br-input-list-name' : 'br-input-list-name'
    }
    onClick={onClick}
    {...input}
  />
)

SetName.propTypes = {
  input: PropTypes.any,
  meta: PropTypes.any,
  onClick: PropTypes.func
}

export const SetNameWithError = withFieldError(SetName)

export const validateRequired = name => {
  if (!name || !name.trim().length) {
    return 'Required'
  }
}

const standaloneTriggers = [
  'Retweet of a Tweet',
  'Like',
  'Send at specific time',
  'Like OR Retweet'
]

function isTriggerStandalone ({ type }) {
  return contains(type, standaloneTriggers)
}
const greaterThan1 = gt(__, 1)
const containsStandaloneTriggers = any(isTriggerStandalone)

const isInvalidTriggerCombination = ifElse(
  propSatisfies(greaterThan1, 'length'),
  containsStandaloneTriggers,
  F
)

const standaloneFacebookTriggers = [
  'Received One-Time Consent',
  'Send at specific time'
]

function isFacebookTriggerStandAlone ({ type }) {
  return contains(type, standaloneFacebookTriggers)
}
const containsFacebookStandaloneTriggers = any(isFacebookTriggerStandAlone)

const isInvalidFacebookTriggerCombination = ifElse(
  propSatisfies(greaterThan1, 'length'),
  containsFacebookStandaloneTriggers,
  F
)

export const validateTriggers = triggers => {
  if (triggers && isInvalidTriggerCombination(triggers)) {
    return (
      <span>
        You can't combine <b>Like</b>, <b>Retweet of a Tweet</b>,{' '}
        <b>Like OR Retweet</b> or <b>Send at a specific time</b> with other
        triggers.
      </span>
    )
  }
  if (triggers && isInvalidFacebookTriggerCombination(triggers)) {
    return (
      <span>
        You can't combine <b>Received One-Time Consent</b> or{' '}
        <b>Send at a specific time</b> with other triggers.
      </span>
    )
  }
}

const noneGnipTriggers = ['Like', 'Send at specific time']

function isTriggerNoneGnip ({ type }) {
  return contains(type, noneGnipTriggers)
}

export const containsNoneGnipTriggers = any(isTriggerNoneGnip)

const MsgAlert = styled.div`
  margin-bottom: 20px;
  padding-left: 15px;
  border-left: 3px solid #c82f3b;

  b {
    color: #c82f3b;
  }
`

export const TriggerError = ({ triggers }) =>
  triggers && containsNoneGnipTriggers(triggers) ? (
    <MsgAlert>
      The fields below should be checked if you've selected
      <br /> <b>Like</b> or <b>Send at a specific time</b>
    </MsgAlert>
  ) : null

TriggerError.propTypes = {
  triggers: PropTypes.array
}

export const validateActions = (currentActions = [], { actions = [] }) => {
  const isOptin = propEq('type', 'Opt user in')
  const isOptout = propEq('type', 'Opt user out')
  const hasOptout = both(Boolean, any(isOptout))
  const hasNoOptoutSet = none(propSatisfies(hasOptout, 'success'))
  const isInvalid = useWith(and, [any(isOptin), hasNoOptoutSet])

  if (isInvalid(currentActions, actions)) {
    return (
      <span>
        If you include the action <b>Opt user in</b>, you also need to add{' '}
        <b>Opt user out</b> in another <b>Trigger and Action Set</b>.
      </span>
    )
  }
}

export const getTriggerActionSetPlatform = (fields, index) => {
  if (fields && fields.getAll) {
    const allFieldValues = fields.getAll()
    const fieldValuesForSet = allFieldValues[index]
    const platform = fieldValuesForSet.platform.toLowerCase()
    return platform
  }

  return 'twitter'
}

const getPlatformTriggers = cond([
  [equals('instagram'), always(InstagramTriggers)],
  [equals('facebook'), always(FacebookTriggers)],
  [T, always(TwitterTriggers)]
])

const getPlatformActions = cond([
  [equals('instagram'), always(InstagramActions)],
  [equals('facebook'), always(FacebookActions)],
  [T, always(TwitterActions)]
])

export const TriggerActionSet = (set, index, fields, isDraft, onApprove) => {
  const currentSetPlatform = getTriggerActionSetPlatform(fields, index)
  const platformTriggers = getPlatformTriggers(currentSetPlatform)
  const platformActions = getPlatformActions(currentSetPlatform)

  return (
    <Panel eventKey={index} key={index}>
      <Panel.Heading className='flex-container expanded-panel'>
        <Panel.Toggle componentClass='a' style={{ flexGrow: '0' }}>
          <i className='fa fa-chevron-down' />
        </Panel.Toggle>

        <Panel.Title style={{ flexGrow: '8', marginLeft: '15px' }}>
          <Field
            name={`${set}.description`}
            component={SetNameWithError}
            onClick={e => {
              e.preventDefault()
            }}
            validate={validateRequired}
          />
        </Panel.Title>

        <Panel.Title style={{ flexGrow: '0' }}>
          {isDraft && (
            <button
              type='button'
              id='brt-experience-save-start'
              className='btn btn green-haze'
              onClick={e => {
                onApprove(index)
                e.stopPropagation()
              }}
            >
              Approve
            </button>
          )}
          <button
            type='button'
            onClick={e => {
              fields.remove(index)
              e.stopPropagation()
            }}
            className='btn dark btn-sm'
          >
            <i className='fa fa-close' />
          </button>
        </Panel.Title>
      </Panel.Heading>
      <Panel.Body collapsible>
        <div className='container-fluid'>
          <div className='row br-set-heading'>
            <div className='col-xs-3 col-sm-3 col-md-4'>
              <h3 className='bold'>{currentSetPlatform.toUpperCase()} set</h3>
            </div>
          </div>

          <div className='row br-set-heading'>
            <div className='col-xs-3 col-sm-3 col-md-4'>
              <h4 className='bold'>Triggers</h4>
            </div>

            <div className='col-xs-9 col-sm-9 col-md-8 col-md-pull-1'>
              <i
                className='fa fa-exclamation-triangle md-24 sm-24 xs-24'
                style={{
                  fontSize: 18,
                  color: 'orange',
                  verticalAlign: 'middle'
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  fontStyle: 'italic',
                  verticalAlign: 'middle'
                }}
              >
                &nbsp; Actions will only fire once the user responds with{' '}
                <span
                  style={{
                    fontWeight: 'bold',
                    textDecorationLine: 'underline'
                  }}
                >
                  all the triggers
                </span>{' '}
                included below
              </span>
            </div>
          </div>

          <div className='row'>
            <FieldArray
              name={`${set}.triggers`}
              component={platformTriggers}
              validate={validateTriggers}
            />
          </div>

          <div className='row br-set-heading'>
            <div className='col-sm-12'>
              <h4 className='bold'>Actions</h4>
            </div>
          </div>
          <div className='row'>
            {/* TODO: convert to bootstrap row and column structure */}
            <FieldArray
              name={`${set}.success`}
              component={platformActions}
              validate={validateActions}
            />
          </div>
        </div>
      </Panel.Body>
    </Panel>
  )
}

export const TriggerActionSets = ({ fields, experiencePlatform }) => (
  <div className='portlet light'>
    <div className='portlet-title'>
      <div
        className='caption'
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <i
          className='fa fa-comment font-green-haze hidden-xs'
          style={{ alignSelf: 'flex-start' }}
        />
        <span className='caption-subject bold font-green-haze uppercase'>
          Triggers and Actions
        </span>
        <div style={{ marginLeft: 'auto' }}>
          {experiencePlatform === 'instagram' && (
            <button
              type='button'
              id='brt-experience-new-instagram-set'
              className='btn green-haze'
              style={{ marginRight: '1rem' }}
              onClick={() =>
                fields.push({
                  platform: 'instagram',
                  description: `Trigger and action set #${fields.length + 1}`
                })
              }
            >
              <i className='fa fa-plus' />
              New Instagram Set
            </button>
          )}
          {experiencePlatform === 'facebook' && (
            <button
              type='button'
              id='brt-experience-new-facebook-set'
              className='btn green-haze'
              style={{ marginRight: '1rem' }}
              onClick={() =>
                fields.push({
                  platform: 'facebook',
                  description: `Trigger and action set #${fields.length + 1}`
                })
              }
            >
              <i className='fa fa-plus' />
              New Facebook Set
            </button>
          )}
          {experiencePlatform === 'twitter' && (
            <button
              type='button'
              id='brt-experience-new-set'
              className='btn green-haze'
              onClick={() =>
                fields.push({
                  platform: 'twitter',
                  description: `Trigger and action set #${fields.length + 1}`
                })
              }
            >
              <i className='fa fa-plus' />
              New Twitter Set
            </button>
          )}
        </div>
      </div>
    </div>

    <div className='portlet-body'>
      <PanelGroup
        accordion
        id='accordion-uncontrolled-example'
        defaultActiveKey={0}
      >
        {fields.length > 0 ? (
          fields.map(TriggerActionSet)
        ) : (
          <div className='br-trigger-action-error has-error-text'>
            <span className='fa fa-exclamation-triangle' /> You must configure
            at least one trigger and action set
          </div>
        )}
      </PanelGroup>
    </div>
  </div>
)

TriggerActionSets.propTypes = {
  fields: PropTypes.any,
  experiencePlatform: PropTypes.string
}

export const TriggerActionSetDrafts = ({
  fields,
  actions,
  experiencePlatform
}) => {
  const onApprove = draftActionIndex => {
    const draft = fields.get(draftActionIndex)
    actions.push(draft)
    fields.remove(draftActionIndex)
  }

  return (
    <div className='portlet light'>
      <div className='portlet-title'>
        <div
          className='caption'
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <i
            className='fa fa-comment font-green-haze hidden-xs'
            style={{ alignSelf: 'flex-start', marginTop: 8 }}
          />
          <span className='caption-subject bold font-green-haze uppercase'>
            To Review
          </span>
          <div style={{ marginLeft: 'auto' }}>
            {experiencePlatform === 'instagram' && (
              <button
                type='button'
                id='brt-experience-new-instagram-set'
                className='btn green-haze'
                style={{ marginRight: '1rem' }}
                onClick={() =>
                  fields.push({
                    platform: 'instagram',
                    description: `Draft trigger set ${fields.length + 1}`
                  })
                }
              >
                <i className='fa fa-plus' />
                New Instagram Set
              </button>
            )}
            {experiencePlatform === 'twitter' && (
              <button
                type='button'
                id='brt-experience-new-set'
                className='btn green-haze'
                onClick={() =>
                  fields.push({
                    platform: 'twitter',
                    description: `Draft trigger set ${fields.length + 1}`
                  })
                }
              >
                <i className='fa fa-plus' />
                New Twitter Set
              </button>
            )}
          </div>
        </div>
      </div>

      <div className='portlet-body'>
        <PanelGroup
          accordion
          id='accordion-uncontrolled-example'
          defaultActiveKey={0}
        >
          {fields.map((...args) => TriggerActionSet(...args, true, onApprove))}
        </PanelGroup>
      </div>
    </div>
  )
}

TriggerActionSetDrafts.propTypes = {
  fields: PropTypes.any,
  actions: PropTypes.any,
  experiencePlatform: PropTypes.string
}

const selector = formValueSelector('widget')

export const Drafts = connect((state, props) => ({
  actions: selector(state, 'actions'),
  ...props
}))(TriggerActionSetDrafts)

const TriggersActionFormComponent = ({ experiencePlatform }) => {
  return (
    <div className='br-actions-and-triggers form-group darkPanel'>
      <div className='col-md-12'>
        <FieldArray
          name='actions'
          component={TriggerActionSets}
          experiencePlatform={experiencePlatform}
        />
        {experiencePlatform === 'twitter' && (
          <FieldArray
            name='draftActions'
            component={Drafts}
            experiencePlatform={experiencePlatform}
          />
        )}
      </div>
    </div>
  )
}

TriggersActionFormComponent.propTypes = {
  experiencePlatform: PropTypes.string
}

export default TriggersActionFormComponent



// WEBPACK FOOTER //
// ./src/pages/widgets/form/configure-triggers-actions/index.js